import React from "react";

interface BackdropLayerProps {
  imageUrl?: string | null;
  videoUrl?: string | null;
  /** When true, shows a subtle "bringing your vision to life" caption */
  isAnimating?: boolean;
}

/**
 * Full-bleed backdrop for DM the Universe sessions. Renders a still image
 * immediately; swaps to a looping muted video when one becomes available.
 * A darkening + slight blur overlay keeps chat bubbles legible.
 */
const BackdropLayer: React.FC<BackdropLayerProps> = ({ imageUrl, videoUrl, isAnimating }) => {
  if (!imageUrl && !videoUrl) return null;

  return (
    <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden">
      {/* Still image — always rendered as a fallback under the video */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Animated loop — fades in when ready */}
      {videoUrl && (
        <video
          key={videoUrl}
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover animate-fade-in"
        />
      )}

      {/* Legibility overlay: darken + soften */}
      <div className="absolute inset-0 bg-background/25" />

      {isAnimating && (
        <div className="absolute bottom-6 right-6 text-xs text-foreground/70 px-3 py-1.5 rounded-full bg-background/40 backdrop-blur-md animate-pulse">
          Bringing your vision to life…
        </div>
      )}
    </div>
  );
};

export default BackdropLayer;
